import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    CreditCard, Star, Trash2, Plus, RefreshCw, Check,
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements, CardElement, useStripe, useElements,
} from '@stripe/react-stripe-js';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input } from '../../components/ui/Form.jsx';
import { StatusPill } from '../../components/ui/Feedback.jsx';

// =============================================================================
// Payment methods tab — Round 7d.1.6
// =============================================================================
// Shows cards saved to this customer via (1) booking engine, (2) dispatcher
// add-card, or (3) future self-serve portal.
//
// Card data never enters our server. Stripe Elements captures it directly
// from the user's browser, returns a token (payment_method_id), and we POST
// only the token to dispatch backend which attaches it to the Stripe customer.
//
// Account billing toggle (7d.3.1) sits below the cards list — cards are the
// preferred method; account billing is a secondary preference.
// =============================================================================

// Load Stripe once at module level — useMemo inside the component would
// recreate on every render. Publishable key is safe to expose.
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export function PaymentMethodsTab({ customerId, customer, isOwner, onCustomerChanged }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { isAdmin } = useCurrentUser();
    const [showAddModal, setShowAddModal] = useState(false);
    const [removingPm, setRemovingPm] = useState(null);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['customer', customerId, 'payment-methods'],
        queryFn: () => api.get(`/customers/${customerId}/payment-methods`),
        enabled: Boolean(customerId),
    });
// Booker visible methods (Slice 9.2). Three-way: 'card' | 'account' | 'both'.
    // Controls what payment options the booker sees at portal checkout.
    // Independent of what's actually on file — dispatch can still charge cards
    // for extras / overdue invoices regardless of this flag.
    //
    // Auto-syncs the legacy account_billing_enabled column so backend rules
    // that still read it (e.g. the show-prices toggle gate) stay consistent:
    //   'card'             → account_billing_enabled = false
    //   'account' | 'both' → account_billing_enabled = true
    const setVisibleMethods = useMutation({
        mutationFn: (next) => api.patch(`/customers/${customerId}`, {
            booker_visible_methods: next,
            account_billing_enabled: next !== 'card',
        }),
        onSuccess: (_data, next) => {
            toast.success(
                next === 'card'    ? 'Booker sees card only'
              : next === 'account' ? 'Booker sees account only'
              :                      'Booker sees both card and account'
            );
            onCustomerChanged?.();
        },
        onError: (err) => toast.error(err.message || 'Could not update'),
    });

    // Show prices to booker (Slice 9.1). Only meaningful when account billing
    // is enabled. Card customers always see prices (they pay by card, they
    // need to see what they're paying). Default false (hide) — most account
    // customers enable account billing precisely because the booker isn't the
    // bill-payer (PA, nanny, etc).
    const togglePricesToBooker = useMutation({
        mutationFn: () => api.patch(`/customers/${customerId}`, {
            show_prices_to_booker: !customer?.show_prices_to_booker,
        }),
        onSuccess: () => {
            toast.success(
                customer?.show_prices_to_booker
                    ? 'Prices hidden from booker'
                    : 'Prices visible to booker'
            );
            onCustomerChanged?.();
        },
        onError: (err) => toast.error(err.message || 'Could not update'),
    });

    const syncMutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/payment-methods/sync`),
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'payment-methods'] });
            if (result.synced > 0) {
                toast.success(`Synced ${result.synced} card${result.synced === 1 ? '' : 's'} from Stripe`);
            } else if (result.message) {
                toast.info(result.message);
            } else {
                toast.success('Already up to date');
            }
        },
        onError: (err) => toast.error(err.message || 'Sync failed'),
    });

    const setDefaultMutation = useMutation({
        mutationFn: (pmId) => api.patch(`/customers/${customerId}/payment-methods/${pmId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'payment-methods'] });
            toast.success('Default card updated');
        },
        onError: (err) => toast.error(err.message || 'Could not update default'),
    });

    const removeMutation = useMutation({
        mutationFn: (pmId) => api.delete(`/customers/${customerId}/payment-methods/${pmId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'payment-methods'] });
            setRemovingPm(null);
            toast.success('Card removed');
        },
        onError: (err) => toast.error(err.message || 'Could not remove card'),
    });

    const paymentMethods = data?.payment_methods || [];

    if (isLoading) {
        return <div className="px-6 py-10 text-center text-sm text-ink-subtle">Loading payment methods…</div>;
    }
    if (isError) {
        return <div className="px-6 py-10 text-center text-sm text-ink-subtle">Could not load payment methods.</div>;
    }

    return (
        <div>
            {/* Header row with Sync + Add actions */}
            <div className="px-6 pt-4 pb-3 flex items-center justify-between gap-3 flex-wrap border-b border-paper-border-soft">
                <p className="text-xs text-ink-subtle">
                    Cards saved with Stripe. We never store card numbers — only the last 4 digits and expiry.
                </p>
                {isAdmin && (
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => syncMutation.mutate()}
                            loading={syncMutation.isPending}
                        >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Sync from Stripe
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => setShowAddModal(true)}
                        >
                            <Plus className="h-3.5 w-3.5" />
                            Add card
                        </Button>
                    </div>
                )}
            </div>

            {paymentMethods.length === 0 ? (
                <div className="px-6 py-10 text-center">
                    <CreditCard className="h-8 w-8 text-ink-subtle mx-auto mb-3" />
                    <p className="text-sm text-ink-muted">No payment methods yet.</p>
                    <p className="text-xs text-ink-subtle mt-1">
                        Cards appear here when a customer books through the website or when a dispatcher adds one.
                    </p>
                </div>
            ) : (
                <div className="divide-y divide-paper-border-soft">
                    {paymentMethods.map((pm) => (
                        <PaymentMethodRow
                            key={pm.id}
                            pm={pm}
                            isAdmin={isAdmin}
                            onSetDefault={() => setDefaultMutation.mutate(pm.id)}
                            onRemove={() => setRemovingPm(pm)}
                            setDefaultPending={setDefaultMutation.isPending && setDefaultMutation.variables === pm.id}
                        />
                    ))}
                </div>
            )}

{/* Booker visible methods — controls what the booker sees at
                portal checkout. Dispatch can use any payment method on file
                regardless of this setting (it's only a portal-visibility
                gate). Owner-only. */}
            <div className="px-6 py-4 border-t border-paper-border-soft">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink">Booker visible payment methods</div>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            What the booker sees at checkout on the customer portal. Dispatch can still charge any saved card for extras or overdue invoices regardless of this setting.
                        </div>
                    </div>
                    {isOwner ? (
                        <div className="flex-shrink-0 inline-flex rounded-md overflow-hidden border border-paper-border" role="radiogroup" aria-label="Booker visible payment methods">
                            {[
                                { value: 'card',    label: 'Card' },
                                { value: 'account', label: 'Account' },
                                { value: 'both',    label: 'Both' },
                            ].map((opt) => {
                                const active = (customer?.booker_visible_methods || 'card') === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        onClick={() => setVisibleMethods.mutate(opt.value)}
                                        disabled={setVisibleMethods.isPending || active}
                                        className={[
                                            'px-3 py-1.5 text-xs font-semibold transition-colors border-r last:border-r-0 border-paper-border',
                                            active
                                                ? 'bg-emerald-600 text-white'
                                                : 'bg-paper text-ink-muted hover:bg-paper-hover hover:text-ink',
                                            setVisibleMethods.isPending && 'opacity-50 cursor-not-allowed',
                                        ].filter(Boolean).join(' ')}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <span className="flex-shrink-0 text-xs text-ink-subtle italic capitalize">
                            {customer?.booker_visible_methods || 'card'} (owner-only)
                        </span>
                    )}
                </div>
            </div>
{/* Show prices to booker — Slice 9.1. Only relevant when account
                is one of the visible methods; card-only customers always see
                prices because they pay by card. Tied to account billing in
                the backend (auto-synced via setVisibleMethods), so the same
                rule still holds: !customer.account_billing_enabled ⇒ prices
                always shown. */}
            {(customer?.booker_visible_methods === 'account' || customer?.booker_visible_methods === 'both') && (
                <div className="px-6 py-4 border-t border-paper-border-soft">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-ink">Show prices to booker</div>
                            <div className="text-xs text-ink-subtle mt-0.5">
                                {customer?.show_prices_to_booker
                                    ? 'The booker sees prices on the customer portal.'
                                    : 'The booker books rides without seeing prices. Useful when the booker (e.g. PA, nanny) isn\u2019t the bill-payer. Owner-only.'}
                            </div>
                        </div>
                        {isOwner ? (
                            <button
                                type="button"
                                onClick={() => togglePricesToBooker.mutate()}
                                disabled={togglePricesToBooker.isPending}
                                className={[
                                    'flex-shrink-0 inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
                                    customer?.show_prices_to_booker
                                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                        : 'bg-paper-hover text-ink-muted hover:bg-paper-border-soft hover:text-ink',
                                    togglePricesToBooker.isPending && 'opacity-50 cursor-not-allowed',
                                ].filter(Boolean).join(' ')}
                                aria-pressed={customer?.show_prices_to_booker === true}
                            >
                                {togglePricesToBooker.isPending
                                    ? 'Saving\u2026'
                                    : customer?.show_prices_to_booker ? 'Visible' : 'Hidden'}
                            </button>
                        ) : (
                            <span className="flex-shrink-0 text-xs text-ink-subtle italic">
                                {customer?.show_prices_to_booker ? 'Visible' : 'Hidden'} (owner-only)
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Add card modal — wraps Stripe Elements provider */}
            {showAddModal && (
                <Elements stripe={stripePromise}>
                    <AddCardModal
                        customerId={customerId}
                        onClose={() => setShowAddModal(false)}
                        onAdded={() => {
                            setShowAddModal(false);
                            queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'payment-methods'] });
                            toast.success('Card added');
                        }}
                    />
                </Elements>
            )}

            {/* Remove confirmation */}
            {removingPm && (
                <RemoveCardModal
                    pm={removingPm}
                    open
                    onClose={() => setRemovingPm(null)}
                    onConfirm={() => removeMutation.mutate(removingPm.id)}
                    isPending={removeMutation.isPending}
                />
            )}
        </div>
    );
}

// =============================================================================
// Row rendering a single card
// =============================================================================
function PaymentMethodRow({ pm, isAdmin, onSetDefault, onRemove, setDefaultPending }) {
    const brandLabel = BRAND_LABELS[pm.brand] || (pm.brand
        ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
        : 'Card');
    const brandLogo = BRAND_LOGOS[pm.brand];                         // local path or null
    const expiryString = pm.exp_month && pm.exp_year
        ? `${String(pm.exp_month).padStart(2, '0')}/${String(pm.exp_year).slice(-2)}`
        : null;
    const isExpired = pm.exp_month && pm.exp_year
        ? isCardExpired(pm.exp_month, pm.exp_year)
        : false;

    return (
        <div className="px-6 py-4 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Brand logo when we have one; text fallback for other brands (Discover, JCB, etc) */}
                    {brandLogo ? (
                        <img
                            src={brandLogo}
                            alt={brandLabel}
                            className="h-7 w-auto flex-shrink-0"
                        />
                    ) : (
                        <span className="text-sm font-medium text-ink">{brandLabel}</span>
                    )}
                    <span className="text-sm font-medium text-ink">
                        •••• {pm.last4 || '????'}
                    </span>
                    {pm.is_default && (
                        <StatusPill status="success">
                            <Star className="h-3 w-3 -mt-0.5" />
                            Default
                        </StatusPill>
                    )}
                    {isExpired && (
                        <StatusPill status="danger">Expired</StatusPill>
                    )}
                    {pm.source && pm.source !== 'engine' && (
                        <StatusPill status="neutral">{pm.source}</StatusPill>
                    )}
                </div>
                <div className="mt-1 text-xs text-ink-muted">
                    {expiryString && (
                        <span className={isExpired ? 'text-status-danger' : ''}>
                            Expires {expiryString}
                        </span>
                    )}
                    {pm.cardholder_name && (
                        <span> · {pm.cardholder_name}</span>
                    )}
                    {pm.funding && (
                        <span> · {pm.funding}</span>
                    )}
                    {pm.country && (
                        <span> · {pm.country}</span>
                    )}
                </div>
                <div className="mt-1 text-2xs text-ink-subtle">
                    Added {formatDate(pm.created_at)}
                </div>
            </div>
            {isAdmin && (
                <div className="flex items-center gap-1 flex-shrink-0">
                    {!pm.is_default && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onSetDefault}
                            disabled={setDefaultPending}
                        >
                            <Check className="h-3.5 w-3.5" />
                            Set default
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-ink-muted hover:text-status-danger"
                        onClick={onRemove}
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// Add card modal — wraps Stripe CardElement + cardholder name
// =============================================================================
// The card input itself lives inside Stripe's iframe. We get a payment_method
// token back from Stripe; that token (not the card) goes to our backend.
// =============================================================================
function AddCardModal({ customerId, onClose, onAdded }) {
    const stripe = useStripe();
    const elements = useElements();
    const api = useApi();
    const [cardholderName, setCardholderName] = useState('');
    const [setAsDefault, setSetAsDefault] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [cardError, setCardError] = useState(null);
    const [formError, setFormError] = useState(null);

    async function handleSubmit() {
        if (!stripe || !elements) {
            setFormError('Card input is still loading — give it a second');
            return;
        }
        setSubmitting(true);
        setFormError(null);
        setCardError(null);

        try {
            const cardElement = elements.getElement(CardElement);
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: cardholderName.trim() || undefined,
                },
            });
            if (error) {
                setCardError(error.message || 'Could not read card');
                setSubmitting(false);
                return;
            }

            // Send the token to our backend. Backend attaches to Stripe customer
            // and persists the reference.
            await api.post(`/customers/${customerId}/payment-methods`, {
                stripe_payment_method_id: paymentMethod.id,
                set_as_default: setAsDefault,
            });

            onAdded();
        } catch (err) {
            setFormError(err.message || 'Could not save card');
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open
            onClose={submitting ? () => {} : onClose}
            title="Add card"
            description="Card details go directly to Stripe — they never touch our servers."
            size="md"
        >
            <div className="space-y-4">
                <Input
                    label="Cardholder name"
                    value={cardholderName}
                    onChange={(e) => setCardholderName(e.target.value)}
                    placeholder="As shown on the card"
                    hint="Optional but recommended for reconciliation."
                />
                <div>
                    <label className="block text-xs text-ink-muted uppercase tracking-wider font-medium mb-1.5">
                        Card details <span className="text-status-danger">*</span>
                    </label>
                    {/* Stripe's iframe card input. Looks styled to match the app
                        via the options prop. */}
                    <div className="border border-paper-border rounded-md px-3 py-3 bg-paper">
                        <CardElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                    {cardError && (
                        <p className="mt-1.5 text-xs text-status-danger">{cardError}</p>
                    )}
                </div>
                <label className="flex items-center gap-2 text-sm text-ink">
                    <input
                        type="checkbox"
                        checked={setAsDefault}
                        onChange={(e) => setSetAsDefault(e.target.checked)}
                        className="rounded border-paper-border"
                    />
                    Set as default payment method
                </label>
                {formError && (
                    <div className="text-xs text-status-danger">{formError}</div>
                )}
                <p className="text-xs text-ink-subtle">
                    By saving this card, the customer authorises Lanz CTS to charge it for this and future transactions
                    per their booking engine terms. This action is audit-logged.
                </p>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={submitting || !stripe}
                >
                    <Plus className="h-4 w-4" />
                    Save card
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Remove confirmation modal
// =============================================================================
function RemoveCardModal({ pm, open, onClose, onConfirm, isPending }) {
    const brandLabel = BRAND_LABELS[pm.brand] || pm.brand || 'Card';
    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Remove card?"
            description={`${brandLabel} ending •••• ${pm.last4} will be detached from this customer. This is audit-logged. The card remains in Stripe but is no longer linked to this customer in dispatch.`}
            size="md"
        >
            {pm.is_default && (
                <div className="bg-status-warning/10 border border-status-warning/30 rounded-md p-3 text-sm text-ink-muted">
                    This is the default card. Removing it will leave this customer without a default until another is set or added.
                </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button
                    variant="danger"
                    onClick={onConfirm}
                    loading={isPending}
                >
                    <Trash2 className="h-4 w-4" />
                    Remove
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Static helpers
// =============================================================================

// Stripe CardElement styling — matches the app's input aesthetic.
const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '14px',
            color: '#1a1a1a',
            '::placeholder': {
                color: '#8a8a8a',
            },
            fontFamily: 'system-ui, -apple-system, sans-serif',
        },
        invalid: {
            color: '#c23939',
        },
    },
};

const BRAND_LABELS = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'Amex',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
    unknown: 'Card',
};

// Official brand logos — shipped with the admin frontend (in /public/card-brands/).
// Keeping them local removes the soft dependency on lanzcts.com and lets the admin
// UI render card logos even if the marketing site is down. Files are the originals
// from Stripe/brand press kits, not renamed, so the filenames look a bit gnarly.
const BRAND_LOGOS = {
    visa:       '/card-brands/VBM_bluRGB_2025.svg',
    mastercard: '/card-brands/ma_symbol.svg',
    amex:       '/card-brands/AXP_BlueBoxLogo_Alternate_REGULARscale_RGB_DIGITAL_700x700.png',
};

function isCardExpired(month, year) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    if (year < currentYear) return true;
    if (year === currentYear && month < currentMonth) return true;
    return false;
}

function formatDate(d) {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
